import { Drawer } from '@mui/material';
import { FC } from 'react';
import { useSidebar } from './context/useSidebar';
import { ChartAdminSidebar } from './ChartAdminSidebar';
import { useScreenSize } from '../../utils';
import ChartTemplateSidebar from './ChartTemplateSidebar';
import FilterSidebarRouter from './FilterSidebarRouter';
import { useGlobalState } from '@intly/core';

/** Exported so a host can reserve this much room when the drawer is
 *  pinned: the docked variant still paints over content, it does not
 *  displace it. */
export const drawerWidth = 360;

export interface SidebarProps {
  /** How far the drawer starts below the top of the viewport, in theme
   *  spacing units, and so how much height it gives up.
   *
   *  Null - the default - clears whatever chrome the host renders above it:
   *  web-app's app bar, and its extra header when one is mounted. A host with
   *  no such chrome passes 0 and gets the full height.
   */
  offsetTop?: number | null;
}

const Sidebar: FC<SidebarProps> = ({ offsetTop = null }) => {
  const { open, onClose, pinned, props, selectedTab } = useSidebar();
  const { entityType } = props;
  const { menuState } = useGlobalState();
  const { smallScreen } = useScreenSize();
  const marginTop = offsetTop ?? (menuState.extraHeaderMounted ? 24 : 18);

  return (
    <Drawer
      anchor={smallScreen ? 'bottom' : 'right'}
      open={open}
      transitionDuration={
        pinned
          ? {
              enter: 0,
              exit: 500,
            }
          : undefined
      }
      variant={pinned && !smallScreen ? 'persistent' : 'temporary'}
      onClose={(event, reason) => {
        onClose();
      }}
      sx={[
        {
          overflow: 'hidden',
          width: {
            xs: '100vw',
            md: drawerWidth,
          },
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            mt: {
              xs: 0,
              md: Math.max(marginTop - 1, 0),
            },
            height: {
              xs: '100vh',
              md: `calc(100vh - ${marginTop * 8}px)`,
            },
            borderTopLeftRadius: {
              xs: 'unset',
              md: 20,
            },
            borderBottomLeftRadius: {
              xs: 'unset',
              md: 20,
            },
            width: {
              xs: '100vw',
              md: drawerWidth,
            },
            boxSizing: 'border-box',
          },
          '& .MuiBackdrop-root': {
            bgcolor: 'unset',
          },
        },
      ]}
    >
      {entityType === 'CHART_ADMIN_PREVIEW' && (
        <ChartAdminSidebar {...props} tab={selectedTab as any} />
      )}
      {entityType === 'CHART_TEMPLATES' && <ChartTemplateSidebar {...props} />}
      {entityType === 'CHART_FILTER' && <FilterSidebarRouter {...props} />}
    </Drawer>
  );
};

export default Sidebar;
